import React from 'react';
import { ActionIcon, Group, Stack } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import DuotoneLightIcon from '../../../components/common/DuotoneLightIcon';
import { DEFAULT_BORDER, DEFAULT_BORDER_COLOR } from '../../../shared/boxes/borderStyles';
import { PxSpacingInput } from './puckPxSpacingField';
import { PuckColorFieldWithPalette } from './createPuckColorField';
import InspectorRow from './InspectorRow';
import { useSelectedItemProps } from './useSelectedItemProps';

/** Toggles de côtés façon Figma : un bouton par bordure, actif = affichée.
 *  Icônes du BorderManager du classique. */
const SIDES = [
  { key: 'top', label: 'Bordure haute', icon: icon({ name: 'border-top', style: 'light' }) },
  { key: 'right', label: 'Bordure droite', icon: icon({ name: 'border-right', style: 'light' }) },
  { key: 'bottom', label: 'Bordure basse', icon: icon({ name: 'border-bottom', style: 'light' }) },
  { key: 'left', label: 'Bordure gauche', icon: icon({ name: 'border-left', style: 'light' }) },
];

/** `defaults` : la bordure que le bloc rend quand rien n'est réglé — sans
 *  elle, le champ affichait celle des box (8 px, quatre côtés) sur un bouton
 *  rendu en pilule sans trait. */
export function BorderControls({ value, onChange, defaults = DEFAULT_BORDER }) {
  const border = { ...defaults, ...(value || {}) };
  const patch = (partial) => onChange({ ...border, ...partial });

  const allOn = SIDES.every(({ key }) => border[key]);

  return (
    <Stack gap={12}>
      <Group gap={4}>
        {/* title natif — pas de Tooltip Mantine : composant flottant qui
            laissait un état hover résiduel sur le premier bouton. */}
        {SIDES.map(({ key, label, icon: sideIcon }) => {
          const active = !!border[key];
          return (
            <ActionIcon
              key={key}
              variant={active ? 'light' : 'subtle'}
              color={active ? 'blue' : 'gray'}
              size="lg"
              title={label}
              aria-label={label}
              aria-pressed={active}
              onClick={() => patch({ [key]: !active })}
            >
              <FontAwesomeIcon icon={sideIcon} style={{ fontSize: 16 }} />
            </ActionIcon>
          );
        })}

        {/* En bout de rangée, comme dans le proposal classique
            (components/common/boxStyleFields/BoxBorderFields.jsx) : c'est le
            même geste au même endroit, sur les deux éditeurs.
            Encadrer une box demandait quatre clics, la désencadrer quatre
            autres. Partiel → on complète, plutôt que d'effacer ce qui vient
            d'être posé. */}
        <ActionIcon
          ml={4}
          variant={allOn ? 'light' : 'subtle'}
          color={allOn ? 'blue' : 'gray'}
          size="lg"
          title="Toutes les bordures"
          aria-label="Toutes les bordures"
          aria-pressed={allOn}
          onClick={() => patch({
            top: !allOn, right: !allOn, bottom: !allOn, left: !allOn,
          })}
        >
          <DuotoneLightIcon name="border-outer" style={{ fontSize: 18 }} />
        </ActionIcon>
      </Group>
      <PxSpacingInput
        label="Arrondi des angles"
        value={border.radius}
        onChange={(next) => patch({ radius: next })}
      />
      <InspectorRow label="Couleur des bordures">
        {/* Affiche la couleur effective (gris défaut si non choisie).
            Le picker émet '' pour « transparent » : sentinelle 'transparent'
            stockée ('' = gris défaut) — la bordure reste dans le layout,
            contrairement aux toggles de côtés qui la retirent. */}
        <PuckColorFieldWithPalette
          label={null}
          value={border.color === 'transparent' ? '' : (border.color || DEFAULT_BORDER_COLOR)}
          onChange={(next) => patch({ color: !next ? 'transparent' : next })}
          allowTransparent
          glyph="stroke"
        />
      </InspectorRow>
    </Stack>
  );
}

/**
 * Champ Puck « Bordures » (objet {radius, color, top, right, bottom, left}) :
 * côtés affichés + arrondi + couleur dans une seule section, façon Figma.
 * Rendu CSS côté composant via borderCssFromProps (utils/borderStyles).
 */
function BorderFieldBody({ value, onChange, readDefaults }) {
  const { itemProps } = useSelectedItemProps();
  const defaults = readDefaults ? readDefaults(itemProps || {}) : DEFAULT_BORDER;
  return <BorderControls value={value} onChange={onChange} defaults={defaults} />;
}

/**
 * `readDefaults(props)` : bordure rendue par le bloc tant que rien n'est
 * réglé (ex. un bouton : pilule sans trait, ou l'ancien `radius`).
 */
export function puckBorderField(label = 'Bordures', { readDefaults } = {}) {
  return {
    type: 'custom',
    label,
    render: ({ value, onChange }) => (
      /* Groupe et non <label> : un <label> s'associe à son premier bouton
         descendant et lui PROPAGE :hover et les clics (spec HTML) — le toggle
         « bordure haute » s'allumait au survol de toute la section. */
      <InspectorRow label={label} layout="stacked">
        <BorderFieldBody value={value} onChange={onChange} readDefaults={readDefaults} />
      </InspectorRow>
    ),
  };
}
